import { useState, useMemo, useId } from "react";
import { Eye, EyeOff, Check, X } from "lucide-react";

const RULES = [
  { key: "length", label: "At least 8 characters", test: (v) => v.length >= 8 },
  { key: "lower", label: "Contains a lowercase letter", test: (v) => /[a-z]/.test(v) },
  { key: "upper", label: "Contains an uppercase letter", test: (v) => /[A-Z]/.test(v) },
  { key: "digit", label: "Contains a number", test: (v) => /\d/.test(v) },
];

/**
 * Password input with visibility toggle, requirement checklist, and optional
 * confirmation matching. All state is controlled by the parent — this
 * component only owns the UI and the "show/hide" and "focus" flags.
 *
 * Props:
 * - value, onChange (required)
 * - label (default: "Password")
 * - placeholder
 * - required
 * - autoComplete: e.g. "current-password" for login, "new-password" for register/reset
 * - showRules: when true, renders the live requirement checklist
 * - minLength: hard HTML5 minLength (defaults to 8 when showRules, else undefined)
 * - testId: base data-testid; toggle button gets `${testId}-toggle`
 * - confirmValue, onConfirmChange, confirmLabel: enable the confirmation field
 */
export function PasswordInput({
  value,
  onChange,
  label = "Password",
  placeholder,
  required = false,
  autoComplete = "current-password",
  showRules = false,
  minLength,
  testId = "password-input",
  confirmValue,
  onConfirmChange,
  confirmLabel = "Confirm password",
  confirmTestId = "confirm-password-input",
}) {
  const [visible, setVisible] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [focused, setFocused] = useState(false);
  const id = useId();
  const confirmId = useId();
  const hasConfirm = typeof onConfirmChange === "function";
  const showConfirmError = hasConfirm && confirmValue.length > 0 && confirmValue !== value;
  const showConfirmOk = hasConfirm && confirmValue.length > 0 && confirmValue === value && value.length > 0;
  const resolvedMin = minLength ?? (showRules ? 8 : undefined);
  const rulesToShow = showRules && (focused || value.length > 0);

  return (
    <div className="space-y-3">
      <div>
        <label htmlFor={id} className="text-sm font-medium mb-1.5 block">{label}</label>
        <div className="relative">
          <input
            id={id}
            type={visible ? "text" : "password"}
            className="sb-input pr-11"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            placeholder={placeholder}
            required={required}
            minLength={resolvedMin}
            maxLength={128}
            autoComplete={autoComplete}
            data-testid={testId}
          />
          <button
            type="button"
            onClick={() => setVisible((v) => !v)}
            data-testid={`${testId}-toggle`}
            aria-label={visible ? "Hide password" : "Show password"}
            aria-pressed={visible}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-[hsl(var(--sb-forest))] focus:ring-offset-1"
            tabIndex={-1}
          >
            {visible ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        {rulesToShow && (
          <ul
            className="mt-2 space-y-1 text-xs rounded-lg bg-muted/40 border border-border/60 px-3 py-2"
            data-testid={`${testId}-rules`}
            aria-live="polite"
          >
            {RULES.map(({ key, label: ruleLabel, test }) => {
              const ok = test(value);
              return (
                <li
                  key={key}
                  className={`inline-flex items-center gap-1.5 mr-3 ${ok ? "text-[hsl(var(--sb-forest-2))]" : "text-muted-foreground"}`}
                  data-testid={`${testId}-rule-${key}`}
                  data-ok={ok ? "true" : "false"}
                >
                  {ok ? <Check size={12} /> : <X size={12} className="opacity-50" />}
                  {ruleLabel}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {hasConfirm && (
        <div>
          <label htmlFor={confirmId} className="text-sm font-medium mb-1.5 block">{confirmLabel}</label>
          <div className="relative">
            <input
              id={confirmId}
              type={confirmVisible ? "text" : "password"}
              className={`sb-input pr-11 ${showConfirmError ? "border-destructive focus:ring-destructive" : ""}`}
              value={confirmValue}
              onChange={(e) => onConfirmChange(e.target.value)}
              placeholder="Re-enter your password"
              required={required}
              maxLength={128}
              autoComplete={autoComplete}
              data-testid={confirmTestId}
              aria-invalid={showConfirmError}
              aria-describedby={showConfirmError ? `${confirmId}-msg` : undefined}
            />
            <button
              type="button"
              onClick={() => setConfirmVisible((v) => !v)}
              data-testid={`${confirmTestId}-toggle`}
              aria-label={confirmVisible ? "Hide password" : "Show password"}
              aria-pressed={confirmVisible}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-[hsl(var(--sb-forest))] focus:ring-offset-1"
              tabIndex={-1}
            >
              {confirmVisible ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {showConfirmError && (
            <p
              id={`${confirmId}-msg`}
              className="mt-1.5 text-xs text-destructive inline-flex items-center gap-1"
              data-testid={`${confirmTestId}-error`}
            >
              <X size={12} /> Passwords don&apos;t match.
            </p>
          )}
          {showConfirmOk && (
            <p
              className="mt-1.5 text-xs text-[hsl(var(--sb-forest-2))] inline-flex items-center gap-1"
              data-testid={`${confirmTestId}-ok`}
            >
              <Check size={12} /> Passwords match.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Validation helper — returns true if the password satisfies every rule in
 * the RULES table. Useful for gating submit buttons alongside HTML5 checks.
 */
export function isPasswordValid(value) {
  return RULES.every((r) => r.test(value));
}
